/**
 * @voltxampere/shared — Locale-aware engineering-notation formatter
 *
 * BLUEPRINT §17.4 closure (ADR-0050 deferred from Sprint 23 → 24).
 *
 * Formats a SI value as `<mantissa> <prefix><unit>` where:
 *   - `<mantissa>` is locale-formatted via `Intl.NumberFormat`
 *     (Türkçe → "1,23"; English → "1.23")
 *   - `<prefix>` is the SI prefix bracketing the magnitude
 *     (G/M/k/—/m/µ/n/p) — locale-independent per SI convention
 *   - `<unit>` is the caller-supplied base unit ("V", "A", "Ω", "Hz")
 *
 * Examples:
 *   formatEngineering(1234.56, "V", "tr")  → "1,23 kV"
 *   formatEngineering(1234.56, "V", "en")  → "1.23 kV"
 *   formatEngineering(0.0045, "A", "tr")   → "4,50 mA"
 *   formatEngineering(0, "V")              → "0 V"
 *   formatEngineering(NaN, "V")            → "—"        (sentinel)
 *
 * Significant-digit policy mirrors the legacy `vxa-inspector`
 * `formatMeasurement` helper so behaviour is unchanged downstream:
 *   - |scaled| ≥ 100 → 1 decimal
 *   - |scaled| ≥ 10  → 2 decimals
 *   - else           → 3 decimals
 *
 * `Intl.NumberFormat` is part of every modern browser + Node 18+;
 * happy-dom (vitest environment) supports it natively.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Sentinel returned when the value is `null`, `NaN`, or `±Infinity`. */
export const ENGINEERING_EMPTY_SENTINEL = "—";
const PREFIXES = [
    { div: 1e9, suffix: "G" },
    { div: 1e6, suffix: "M" },
    { div: 1e3, suffix: "k" },
    { div: 1, suffix: "" },
    { div: 1e-3, suffix: "m" },
    { div: 1e-6, suffix: "µ" /* µ */ },
    { div: 1e-9, suffix: "n" },
    { div: 1e-12, suffix: "p" },
];
function pickFractionDigits(scaled) {
    const abs = Math.abs(scaled);
    if (abs >= 100)
        return 1;
    if (abs >= 10)
        return 2;
    return 3;
}
/**
 * Format a numeric quantity as `<mantissa> <prefix><unit>`. The
 * mantissa is locale-aware (Türkçe decimal "," vs English ".");
 * the SI prefix is universal.
 */
export function formatEngineering(value, unit, locale = "en") {
    if (value === null || value === undefined)
        return ENGINEERING_EMPTY_SENTINEL;
    if (!Number.isFinite(value))
        return ENGINEERING_EMPTY_SENTINEL;
    if (value === 0)
        return `0 ${unit}`;
    const abs = Math.abs(value);
    for (const { div, suffix } of PREFIXES) {
        if (abs >= div) {
            const scaled = value / div;
            const fractionDigits = pickFractionDigits(scaled);
            const formatter = new Intl.NumberFormat(locale, {
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
                useGrouping: false,
            });
            return `${formatter.format(scaled)} ${suffix}${unit}`;
        }
    }
    // Below pico — fall back to scientific notation. Number.toExponential
    // is ASCII (".") so wrap it through Intl for the same locale rules.
    const formatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: false,
        notation: "scientific",
    });
    return `${formatter.format(value)} ${unit}`;
}
/**
 * Format a plain numeric quantity (no SI prefix) — useful for
 * dimensionless counts, percentages, gain ratios. Locale-aware.
 */
export function formatNumber(value, locale = "en", options = {}) {
    if (value === null || value === undefined)
        return ENGINEERING_EMPTY_SENTINEL;
    if (!Number.isFinite(value))
        return ENGINEERING_EMPTY_SENTINEL;
    return new Intl.NumberFormat(locale, options).format(value);
}
//# sourceMappingURL=format-engineering.js.map